import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Input, Text, Link, Stack, HStack } from '@chakra-ui/react';
import { Checkbox, CheckboxGroup, Fieldset, For } from '@chakra-ui/react';
import { Search } from 'lucide-react';
import lunr from 'lunr';

// ---------------------------------------------------------------------------
// N-gram pipeline — must be registered before lunr.Index.load() is called.
// The function name 'ngram' must match the label used in build-search-index.js.
// ---------------------------------------------------------------------------
function ngramTokenize(token) {
    const str = token.toString();
    const N = 3;
    if (str.length < N) return token;
    const result = [token];
    for (let i = 0; i <= str.length - N; i++) {
        const gram = str.slice(i, i + N);
        if (gram !== str) result.push(token.clone(() => gram));
    }
    return result;
}
lunr.Pipeline.registerFunction(ngramTokenize, 'ngram');

export default function ImpactReportSearchForm({ onNavigate }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [selectedYears, setSelectedYears] = useState([]);
    const [results, setResults] = useState([]);
    const [indexReady, setIndexReady] = useState(false);

    const idxRef = useRef(null);
    const docsRef = useRef({});

    useEffect(() => {
        Promise.all([
            fetch('/search-index.json').then(r => r.json()),
            fetch('/search-docs.json').then(r => r.json()),
        ]).then(([rawIndex, docs]) => {
            idxRef.current = lunr.Index.load(rawIndex);
            docsRef.current = docs;
            setIndexReady(true);
        }).catch(err => {
            console.error('Failed to load search index:', err);
        });
    }, []);

    useEffect(() => {
        if (!indexReady || !query.trim()) {
            setResults([]);
            return;
        }
        try {
            const hits = idxRef.current.search(query.trim());
            const filtered = hits
                .filter(r => selectedYears.length === 0 || selectedYears.includes(docsRef.current[r.ref]?.year))
                .map(r => ({ id: r.ref, ...docsRef.current[r.ref] }))
                .filter(d => d.title);
            setResults(filtered);
        } catch {
            setResults([]);
        }
    }, [query, selectedYears, indexReady]);

    return (
        <Box>
            <Box>
                <Box 
                    position="relative"
                >
                    <Box
                        position="absolute"
                        left="3"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                    >
                        <Search size={24} />
                    </Box>
                    <Input
                        pt="6"
                        pb="6"
                        pl="11"
                        fontSize="md"
                        placeholder="Search..."
                        aria-label="Search impact reports"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                </Box>
                <Box
                    display={'none'}
                >
                    <Fieldset.Root>
                        <CheckboxGroup
                            name="report-year"
                            value={selectedYears}
                            onValueChange={setSelectedYears}
                        >
                            <Fieldset.Legend fontSize="sm" mb="1" mt={3}>
                                Select a year to filter results:
                            </Fieldset.Legend>
                            <Fieldset.Content>
                                <HStack align="flex-start">
                                    <For each={["2025", "2024"]}>
                                        {(value) => (
                                            <Stack key={value} align="flex-start" flex="1">
                                                <Checkbox.Root value={value} size="sm">
                                                    <Checkbox.HiddenInput />
                                                    <Checkbox.Control />
                                                    <Checkbox.Label>{value}</Checkbox.Label>
                                                </Checkbox.Root>
                                            </Stack>
                                        )}
                                    </For>
                                </HStack>
                            </Fieldset.Content>
                        </CheckboxGroup>
                    </Fieldset.Root>
                </Box>
            </Box>
            <Box mt="4" maxH="60vh" overflowY="auto" aria-live="polite" aria-atomic="true">
                {query.trim() && indexReady && results.length === 0 && (
                    <Text color="gray.500" fontSize="sm">No results found for &ldquo;{query}&rdquo;.</Text>
                )}
                {results.length > 0 && (
                    <Stack gap="2">
                        {results.map(doc => (
                            <Link
                                key={doc.id}
                                href={`${doc.url}#${doc.anchor}`}
                                display="block"
                                _hover={{ textDecoration: 'none' }}
                                onClick={(e) => {
                                    const isCrossPage = window.location.pathname !== doc.url;
                                    if (isCrossPage) {
                                        // Prevent immediate navigation. If the <a> href triggers
                                        // React Router before the dialog's exit animation finishes,
                                        // Ark UI's body scroll lock is orphaned on the new page and
                                        // Chrome silently blocks scrollIntoView. Close the dialog
                                        // first, wait for the exit animation (~200ms) to clear the
                                        // scroll lock, then navigate programmatically.
                                        e.preventDefault();
                                        onNavigate?.();
                                        setTimeout(() => {
                                            // Must be set before navigate() calls pushState so Chrome
                                            // does not queue a scroll-to-0 for the new history entry.
                                            // If set after pushState, Chrome has already scheduled its
                                            // async scroll restoration and 'manual' is too late.
                                            window.history.scrollRestoration = 'manual';
                                            navigate(`${doc.url}#${doc.anchor}`);
                                        }, 300);
                                    } else {
                                        // Same-page hash change: dialog stays mounted so Ark UI
                                        // properly cleans up the scroll lock during its animation.
                                        onNavigate?.();
                                    }
                                }}
                            >
                                <Box
                                    p="3"
                                    borderWidth="1px"
                                    borderRadius="md"
                                    _hover={{ bg: 'gray.50' }}
                                >
                                    <Text fontWeight="semibold" fontSize="sm" lineClamp={1}>{doc.title}</Text>
                                    <Text fontSize="xs" color="gray.500" mb="1">{doc.pageTitle} &middot; {doc.year}</Text>
                                    <Text fontSize="xs" color="gray.700" lineClamp={2}>{doc.body}</Text>
                                </Box>
                            </Link>
                        ))}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}